import { useStudentNationalReadiness } from '../../../api/hooks';
import { Badge, EmptyState } from '../../../components/ui';
import { FaChartLine } from '../../../components/ui/icons';
import Box from '../components/Box';
import { FAILED, Figure, LOADING, PctBar, pct } from './shared';

/** How ready the student is for the national exam: mastery on the chapters it is expected to cover. */
export default function NationalView({ id, studentName }: { id: number; studentName: string }) {
  const q = useStudentNationalReadiness(id);
  if (q.isLoading) return LOADING;
  if (q.isError || !q.data) return FAILED;
  const d = q.data;
  if (!d.available) {
    return <EmptyState compact icon={<FaChartLine />} title="No national exam forecast yet"
      body="Readiness appears once past national exam papers exist for this student's subjects." />;
  }
  return (
    <div className="space-y-4">
      <p className="text-[13px] text-muted-2">
        Based on the {d.national_exam?.name} forecast: each subject's chapters weighted by how many questions they are expected to get,
        against {studentName}'s results on those chapters so far.
      </p>
      {d.subjects.map((s) => (
        <Box key={s.subject_id} title={s.name} help="Readiness: mastery on the chapters expected in the national exam, weighted by their expected share. Coverage: the share of expected questions whose chapters the student has been tested on.">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Figure label="Readiness" value={pct(s.readiness)} tone={s.readiness == null ? undefined : s.readiness >= 50 ? 'good' : 'bad'}
              sub={s.readiness == null ? 'Not tested on these chapters yet' : 'On the chapters tested so far'} />
            <Figure label="Coverage" value={pct(s.coverage)} sub="of the expected questions' chapters tested" />
            <Figure label="Past papers" value={s.papers ?? '—'} sub="used for the forecast" />
          </div>
          {s.focus.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">Focus next</p>
              <ul className="flex flex-wrap gap-2">
                {s.focus.map((c) => (
                  <li key={c.chapter_id} className="rounded-full border border-accent-line bg-accent-bg px-3 py-1 text-[13px] text-accent-ink">
                    {c.name} <span className="text-xs">· ~{c.share} of 100 · {c.mastery == null ? 'not tested' : pct(c.mastery)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ul className="mt-4 space-y-2.5">
            {s.chapters.filter((c) => c.share > 0).slice(0, 10).map((c) => (
              <li key={c.chapter_id}>
                <div className="mb-1 flex flex-wrap justify-between gap-2 text-[13px]">
                  <span className="text-ink">{c.name}{c.trend === 'up' && <Badge tone="warning" size="sm" className="ml-1.5">Rising</Badge>}{c.trend === 'due' && <Badge tone="warning" size="sm" className="ml-1.5">Due</Badge>}</span>
                  <span className="font-mono text-ink">{c.mastery == null ? <span className="text-muted">not tested</span> : pct(c.mastery)}<span className="text-muted"> · ~{c.share} questions</span></span>
                </div>
                <PctBar value={c.mastery} tone={c.mastery == null ? 'slate' : c.mastery >= 60 ? 'success' : 'danger'} label={`${c.name}: ${c.mastery == null ? 'not tested' : pct(c.mastery)}, about ${c.share} of 100 questions`} />
              </li>
            ))}
          </ul>
        </Box>
      ))}
    </div>
  );
}
